import React from "react";
import PropTypes from "prop-types";
import {
  Card,
  Box,
  Typography,
  Stack,
  Tooltip,
} from "@mui/material";
import {
  CalendarMonthOutlined as CalendarIcon,
  ScheduleOutlined as ClockIcon,
  HourglassBottomOutlined as TimerIcon,
  // RocketLaunchOutlined as DeployedIcon,
  PublicOutlined as NetworkIcon,
  InfoOutlined as InfoIcon,
} from "@mui/icons-material";
import moment from "moment";
import { colors, borderRadiusPx } from "../../../../../theme";
import { getNetworkName } from "../../../../../static/Constants";

const formatRolloverDuration = (seconds) => {
  if (!seconds || Number(seconds) <= 0) return "N/A";
  const numSec = Number(seconds);
  const days = Math.floor(numSec / (60 * 60 * 24));
  const hours = Math.floor((numSec % (60 * 60 * 24)) / (60 * 60));
  const minutes = Math.floor((numSec % (60 * 60)) / 60);

  let formattedTime = "";
  if (days > 0) formattedTime += `${days}d `;
  if (hours > 0) formattedTime += `${hours}h `;
  if (minutes > 0) formattedTime += `${minutes}m`;
  return formattedTime.trim() || "< 1m";
};

const LotteryScheduleCard = ({ lottery, datelist, networkId }) => {
  const isRolloverGame =
    lottery?.lotteryType === "2" || lottery?.lotteryType === "3";

  const startTimeFormatted = datelist?.startTime
    ? moment.unix(Number(datelist.startTime)).format("DD/MM/YYYY hh:mm A")
    : "N/A";

  const drawTimeFormatted = datelist?.drawTime
    ? moment.unix(Number(datelist.drawTime)).format("DD/MM/YYYY hh:mm A")
    : "N/A";

  // const deployedDateFormatted = lottery?.deployedOn
  //   ? moment.unix(Number(lottery.deployedOn)).format("DD/MM/YYYY")
  //   : "N/A";

  const networkName = getNetworkName(networkId);

  return (
    <Card
      className="lottery-glass-card"
      sx={{
        p: { xs: 2.5, sm: 3 },
        borderRadius: borderRadiusPx.xl,
        backgroundColor: colors.dashboard.cardBg,
        border: `1px solid ${colors.dashboard.cardBorder}`,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2.5 }}>
        <CalendarIcon sx={{ color: colors.primary.main, fontSize: 22 }} />
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: colors.slate[900],
            fontSize: "1.05rem",
          }}
        >
          Timeline & Schedule
        </Typography>
      </Stack>

      <Stack spacing={1.5} sx={{ flexGrow: 1 }}>
        {/* Start Time */}
        <Box className="lottery-spec-row">
          <Stack direction="row" spacing={1} alignItems="center">
            <ClockIcon sx={{ color: colors.emerald[600], fontSize: 18 }} />
            <Typography variant="body2" sx={{ color: colors.slate[700], fontWeight: 500 }}>
              Start Time
            </Typography>
          </Stack>
          <Typography variant="body2" sx={{ fontWeight: 600, color: colors.slate[900] }}>
            {startTimeFormatted}
          </Typography>
        </Box>

        {/* Draw / Execution Time */}
        <Box className="lottery-spec-row">
          <Stack direction="row" spacing={1} alignItems="center">
            <TimerIcon sx={{ color: colors.amber[600], fontSize: 18 }} />
            <Typography variant="body2" sx={{ color: colors.slate[700], fontWeight: 500 }}>
              Draw / Execution Time
            </Typography>
            <Tooltip title="Scheduled on-chain VRF draw execution timestamp.">
              <InfoIcon sx={{ color: colors.slate[400], fontSize: 14, cursor: "pointer" }} />
            </Tooltip>
          </Stack>
          <Typography variant="body2" sx={{ fontWeight: 600, color: colors.amber[800] }}>
            {drawTimeFormatted}
          </Typography>
        </Box>

        {/* Rollover Duration */}
        {isRolloverGame && (
          <Box className="lottery-spec-row">
            <Stack direction="row" spacing={1} alignItems="center">
              <ClockIcon sx={{ color: colors.cyan[600], fontSize: 18 }} />
              <Typography variant="body2" sx={{ color: colors.slate[700], fontWeight: 500 }}>
                Rollover Cycle Interval
              </Typography>
              <Tooltip title="Duration added to the lottery countdown when a draw rolls over.">
                <InfoIcon sx={{ color: colors.slate[400], fontSize: 14, cursor: "pointer" }} />
              </Tooltip>
            </Stack>
            <Typography variant="body2" sx={{ fontWeight: 600, color: colors.cyan[700] }}>
              {formatRolloverDuration(datelist?.rolloverdays)}
            </Typography>
          </Box>
        )}

        {/* Deployed On - COMMENTED OUT */}
        {/* <Box className="lottery-spec-row">
          <Stack direction="row" spacing={1} alignItems="center">
            <DeployedIcon sx={{ color: colors.primary.main, fontSize: 18 }} />
            <Typography variant="body2" sx={{ color: colors.slate[700], fontWeight: 500 }}>
              Deployed Date
            </Typography>
          </Stack>
          <Typography variant="body2" sx={{ fontWeight: 600, color: colors.slate[900] }}>
            {deployedDateFormatted}
          </Typography>
        </Box> */}

        {/* Network Ecosystem */}
        <Box className="lottery-spec-row">
          <Stack direction="row" spacing={1} alignItems="center">
            <NetworkIcon sx={{ color: colors.purple[600], fontSize: 18 }} />
            <Typography variant="body2" sx={{ color: colors.slate[700], fontWeight: 500 }}>
              Blockchain Network
            </Typography>
          </Stack>
          <Typography variant="body2" sx={{ fontWeight: 600, color: colors.purple[700] }}>
            {networkName}
          </Typography>
        </Box>
      </Stack>
    </Card>
  );
};

LotteryScheduleCard.propTypes = {
  lottery: PropTypes.object,
  datelist: PropTypes.object,
  networkId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default LotteryScheduleCard;
